import { GuidEmpty, MaxFileSizeStrFormat, MaxTicks, TimeSpanMaxValue } from 'mbs-ui-kit/utils/constants';
import { MinDate } from 'mbs-ui-shared@constants/c-sharp-constants';
import { AzureAccessTierType, StorageType, TypesForFFI, TypesForSynthetic } from 'mbs-ui-shared@models';
import { ScheduleStepValue, ScheduleType } from 'mbs-ui-shared@models/backup/schedule-models';
import { PlanTypes } from 'mbs-ui-shared@models/PlanTypes.enum';
import { RecurType } from 'mbs-ui-shared@models/RecurType.enum';
import { RecurringSchedule } from 'mbs-ui-shared@models/ScheduleModel';
import { DefaultSelectData } from 'mbs-ui-shared@models/SelectData';
import { StorageConnection } from 'mbs-ui-shared@models/storge-connections';
import { firstNumberGreaterThanSecondIfNotFloat } from 'mbs-ui-shared@utils/bigMath';
import { TimeSpanToTimeGroup, baseDateFormat, dateFormatWithoutT, getDateWithoutZ } from 'mbs-ui-shared@utils/date';
import { ScheduleDataHelper } from 'mbs-ui-shared@utils/schedule-data-helper';
import moment from 'moment';
import { Duration } from 'moment/moment';
import { AdvancedOptionsStepValue, DataForAdvancedStep } from 'wizards/models/advanced-options-models';
import { RestoreTypes } from 'wizards/models/base/restore-types-model';
import { ClusterStepValue } from 'wizards/models/cluster-models';
import { NotificationsStepValue } from 'wizards/models/notifications-models';
import { SelectVirtualDisksStepValue, VirtualDisk, VirtualDisksSelectedType } from 'wizards/models/select-virtual-disks-models';
import {
  SelectVirtualMachinesStepValue,
  VirtualMachinesSelectedType,
  VirtualMachinesSelectedWithNumbersType
} from 'wizards/models/select-virtual-machines-models';
import { ProvisionModeEnum, VmSettingsStepValue } from 'wizards/models/vm-settings-models';
import { VolumeUsageStateEnum, WhatBackupStepValue } from 'wizards/models/what-backup-models';
import { WhatBackupTreeStepValue } from 'wizards/models/what-backup-tree-model';
import { AdvancedFilterStepValue } from '../../models/advanced-filters-models';
import { AdvancedSettingsVirtualStepValue } from '../../models/advanced-settings-vm-models';
import { AppAwareStepValue } from '../../models/app-aware-models';
import { ArchiveConsistencyStepValue, RunRestoreVerification, defaultFailureMinutes } from '../../models/archive-consistency-step-value';
import { HyperVMachineExType } from '../../models/base/base-hyper-v-plan-model';
import { DiskInfoCommunication } from '../../models/base/base-models/plan-disk-info-model';
import { VirtualMachineExType } from '../../models/base/base-vmware-plan-model';
import { CompressionEncryptionStepValue } from '../../models/compression-encription-models';
import { PlanNameStepValue } from '../../models/plan-name-models';
import { PrePostActionsStepValue } from '../../models/pre-post-actions-models';
import { RetentionPolicyStepValue, getDefaultGFSPolicySettings } from '../../models/retention-policy-models';
import { ScheduleAdvancedStepValue, ScheduleFormSettings, ScheduleSettings } from '../../models/schedule-advanced-models';
import { SelectHostStepValue } from '../../models/select-host-models';
import { SimpleScheduleStepValue } from '../../models/simple-schedule-models';
import { SQLServerInstanceAuthenticationTypeEnum, SQLServerInstanceStepValue } from '../../models/sql-server-instance-models';
import { SQLSourceDatabasesStepValue } from '../../models/sql-source-databases-models';
import { WhereBackupStepValue } from '../../models/where-backup-models';
import { SupportMethodsForStepsFromPlan } from '../support-methods-for-steps-from-plan';

export class StepsDataFromPlanHelper {
  public static updatePlanNameStep(planName: string, saveInCloud: boolean, isNBF = false): PlanNameStepValue {
    return { Name: planName || '', SavePlanInCloud: saveInCloud || isNBF, valid: true };
  }

  public static updateWhereBackup(IsHybridBackup: boolean, HybridID: string, ConnectionID: string): WhereBackupStepValue {
    const hybridNotEmpty = !!HybridID && HybridID !== GuidEmpty;
    const connectionNotEmpty = !!ConnectionID && ConnectionID !== GuidEmpty;
    const valid = !IsHybridBackup ? hybridNotEmpty || connectionNotEmpty : hybridNotEmpty && connectionNotEmpty;

    return { IsHybridBackup: IsHybridBackup, HybridID: HybridID, ConnectionID: ConnectionID, valid };
  }

  public static updateWhatBackup(planWrapper: any, needFilter = false): WhatBackupStepValue {
    let diskVolumes = planWrapper.DiskVolumes?.map((x) => {
      return {
        ...x,
        IdentityAndDiskId: x.Identity + x.DiskId
      };
    });

    const removedMap =
      diskVolumes?.reduce((acc, next) => {
        if (next.VolumeUsageState === +VolumeUsageStateEnum.Removed) acc[next.IdentityAndDiskId] = next;

        return acc;
      }, {}) || {};
    const diskVolumesFromDiskInfo: any = {};
    diskVolumes =
      (needFilter
        ? diskVolumes?.filter((volume) => volume.Enabled && volume.VolumeUsageState !== +VolumeUsageStateEnum.Removed)
        : diskVolumes?.filter((volume) => volume.VolumeUsageState !== +VolumeUsageStateEnum.Removed)) || [];

    diskVolumes.forEach(
      (diskVolume) =>
        removedMap[diskVolume.IdentityAndDiskId]?.Enabled &&
        removedMap[diskVolume.IdentityAndDiskId]?.MountPointsInfo === diskVolume.MountPointsInfo &&
        (diskVolume.Enabled = true)
    );

    if (planWrapper.plan.DiskInfo && planWrapper.plan.DiskInfo.length) {
      planWrapper.plan.DiskInfo.forEach((next) => {
        if (next.Volumes && next.Volumes.length) {
          next.Volumes.forEach((volume) => {
            const v = { ...volume, IdentityAndDiskId: volume.Identity + volume.DiskId };
            diskVolumesFromDiskInfo[v.IdentityAndDiskId] = v;
            diskVolumesFromDiskInfo[v.IdentityAndDiskId].DiskDriveType = next.DriveType;
            if (!diskVolumesFromDiskInfo[v.IdentityAndDiskId].DriveType)
              diskVolumesFromDiskInfo[v.IdentityAndDiskId].DriveType = next.DriveType;
          });
        }
      });
    }
    (diskVolumes || []).forEach((x) => {
      x.IdentityForUiField = x.Identity + x.DiskId + x.WindowsVolumeIdentity + x.ExtentBasedId;
      x.DriveType = diskVolumesFromDiskInfo[x.IdentityAndDiskId] ? diskVolumesFromDiskInfo[x.IdentityAndDiskId].DriveType : '';
      x.DiskDriveType = diskVolumesFromDiskInfo[x.IdentityAndDiskId] ? diskVolumesFromDiskInfo[x.IdentityAndDiskId].DiskDriveType : '';
    });
    return {
      BackupVolumes: planWrapper.plan.BackupVolumes || 1,
      partitions: diskVolumes?.filter((d) => d.VolumeUsageState !== +VolumeUsageStateEnum.Removed),
      valid: false
    };
  }

  public static updateWhatBackupTree(newPlan): WhatBackupTreeStepValue {
    return {
      includeFolders: (newPlan.Items || []).map((i) => i.Path),
      excludeFolders: (newPlan.ExcludedItems || []).map((i) => i.Path),
      valid: true
    };
  }

  public static updateCompressionEncryptionFroConsistencyCheck(newPlan): CompressionEncryptionStepValue {
    return {
      skipLegacy: newPlan.SkipOldFormatFilesProcessing,
      UseEncryption: newPlan.UseEncryption,
      EncryptionPassword: newPlan.UseEncryption ? newPlan.EncryptionPassword || '' : '',
      valid: true
    };
  }

  public static updateCompressionEncryption(
    newPlan,
    isNBF = false,
    isUnix = false,
    isCreate = false,
    isVirtual = false
  ): CompressionEncryptionStepValue {
    const newObject: CompressionEncryptionStepValue = {
      UseCompression: newPlan.UseCompression,
      UseEncryption: newPlan.UseEncryption,
      EncryptionAlgorithm: newPlan.EncryptionAlgorithm,
      EncryptionKeySize: newPlan.EncryptionKeySize,
      EncryptionPassword: '',
      valid: true
    };
    if (!isVirtual) newObject.UseServerSideEncryption = newPlan.UseServerSideEncryption;

    if (newPlan.UseEncryption) {
      const pass = newPlan.EncryptionPassword || '';
      newObject.EncryptionPassword = pass;
    }

    if (isNBF) newObject.hint = newPlan.HintString || '';
    else if (!isUnix && !(newPlan.UseFileNameEncryption === null || newPlan.UseFileNameEncryption === undefined))
      newObject.UseFileNameEncryption = newPlan.UseFileNameEncryption;

    return newObject;
  }

  public static updateArchiveConsistency(newPlan, isIbb = false): ArchiveConsistencyStepValue {
    const step: ArchiveConsistencyStepValue = { useFullConsistencyCheck: !!newPlan.UseFullConsistencyCheck, valid: true };
    if (isIbb) {
      step.RunRestoreVerificationOn = newPlan.RunRestoreVerificationOn || RunRestoreVerification.None;
      const duration = moment.duration({ second: newPlan.FrameRateSeconds || 0 });
      step.minutes = duration.minutes();
      step.seconds = duration.seconds();
      step.failureMinutes = !(newPlan.CheckTimeSeconds === null || newPlan.CheckTimeSeconds === undefined)
        ? newPlan.CheckTimeSeconds / 60
        : defaultFailureMinutes;

      if (newPlan.TestVmSettings) {
        step.numCpu = newPlan.TestVmSettings.NumCpu;
        step.memoryInMb = newPlan.TestVmSettings.MemoryInMb;
        if (newPlan.TestVmSettings.DynamicMemoryEnabled) {
          step.dynamicMemory = {
            enabled: true,
            min: newPlan.TestVmSettings.MinMemoryInMb,
            max: newPlan.TestVmSettings.MaxMemoryInMb
          };
        }
      } else {
        step.numCpu = 2;
        step.memoryInMb = 4096;
      }
    }
    return step;
  }

  public static updateNBFHybridRetentionPolicy(newPlan, step: RetentionPolicyStepValue): RetentionPolicyStepValue {
    const hybridConnection = newPlan.HybridConnections?.[0];

    if (hybridConnection) {
      const retentionTime = hybridConnection.RetentionTime;
      const equalMaxTime =
        retentionTime && (retentionTime === TimeSpanMaxValue || (retentionTime.Ticks && retentionTime.Ticks === MaxTicks));
      const timeGroup = !equalMaxTime && retentionTime ? TimeSpanToTimeGroup(retentionTime) : { value: 3, timePeriod: 2 };

      step.HybridRetentionPolicy = {
        deleteVersionsOlderThan: !step.RetentionUseDefaultSettings,
        deleteVersionsOlderThanCount: step.deleteVersionsOlderThanCount,
        deleteVersionsOlderThanPeriod: step.deleteVersionsOlderThanPeriod
      };

      step.RetentionUseDefaultSettings = !(!!retentionTime && !equalMaxTime);
      step.deleteVersionsOlderThanCount = timeGroup.value;
      step.deleteVersionsOlderThanPeriod = timeGroup.timePeriod;
    }

    return step;
  }

  public static updateNBFRetentionPolicy(
    newPlan,
    needShowImmutability: boolean,
    isCreate = false,
    isUnix = false,
    isNBF = false
  ): RetentionPolicyStepValue {
    let retentionTime = newPlan.RetentionTime || newPlan.SerializationSupportRetentionTime;
    if (newPlan.SerializationSupportRetentionTime && newPlan.SerializationSupportRetentionTime !== TimeSpanMaxValue) {
      retentionTime = newPlan.SerializationSupportRetentionTime;
    }
    const equalMaxTime = retentionTime && (retentionTime === TimeSpanMaxValue || (retentionTime.Ticks && retentionTime.Ticks === MaxTicks));
    const timeGroup = !equalMaxTime && retentionTime ? TimeSpanToTimeGroup(retentionTime) : { value: 3, timePeriod: 2 };
    const step: RetentionPolicyStepValue = {
      RetentionUseDefaultSettings:
        newPlan.RetentionUseDefaultSettings || equalMaxTime || (!newPlan.RetentionTime && !newPlan.SerializationSupportRetentionTime),
      deleteVersionsOlderThanCount: timeGroup.value,
      deleteVersionsOlderThanPeriod: timeGroup.timePeriod,
      intelligentRetention: newPlan.AutomaticallyReduceEarlyDeletionFee,
      GFSPolicySettings: getDefaultGFSPolicySettings(),
      valid: true
    };
    if (newPlan.ForwardIncremental) {
      step.RetentionUseDefaultSettings = false;
    }
    if (!isCreate && !(newPlan.GFSPolicySettings === null || newPlan.GFSPolicySettings === undefined)) {
      step.GFSPolicySettings.enabled = newPlan.GFSPolicySettings.IsEnabled;
      if (newPlan.GFSPolicySettings.IsEnabled) {
        if (needShowImmutability) step.GFSPolicySettings.enabledImmutability = newPlan.GFSPolicySettings.ImmutabilityEnabled;
        if (newPlan.GFSPolicySettings.Weekly) {
          step.GFSPolicySettings.weekly = {
            enabled: newPlan.GFSPolicySettings.Weekly.IsEnabled,
            storePeriod: newPlan.GFSPolicySettings.Weekly.StorePeriod
          };
        }
        if (newPlan.GFSPolicySettings.Monthly) {
          step.GFSPolicySettings.monthly = {
            enabled: newPlan.GFSPolicySettings.Monthly.IsEnabled,
            storePeriod: newPlan.GFSPolicySettings.Monthly.StorePeriod
          };
        }
        if (newPlan.GFSPolicySettings.Yearly) {
          step.GFSPolicySettings.yearly = {
            enabled: newPlan.GFSPolicySettings.Yearly.IsEnabled,
            storePeriod: newPlan.GFSPolicySettings.Yearly.StorePeriod,
            takeBackupFromMonth: newPlan.GFSPolicySettings.Yearly.TakeBackupFromMonth
          };
        }
      }
    }

    if (isCreate && isNBF) {
      step.RetentionUseDefaultSettings = false;
      step.deleteVersionsOlderThanPeriod = 2;
      step.deleteVersionsOlderThanCount = 3; // MBS-22664 3 month by Default
      step.intelligentRetention = true;
    }

    return step;
  }

  public static updateRetentionPolicy(
    newPlan,
    isCreate: boolean,
    planType: PlanTypes = PlanTypes.BackupDiskImagePlan,
    isUnix = false
  ): RetentionPolicyStepValue {
    const helpData = SupportMethodsForStepsFromPlan.getRetentionPolicyHelpData(newPlan);
    if (planType === PlanTypes.Plan || planType === PlanTypes.DataBasePlan) {
      return this.updateRetentionPolicyFileOrSQL(newPlan, helpData, isCreate, isUnix);
    }

    const RetentionPolicy: RetentionPolicyStepValue = {
      RetentionUseDefaultSettings: newPlan.RetentionUseDefaultSettings,
      keepNumberOfVersions: !helpData.equalMax,
      RetentionNumberOfVersions:
        helpData.equalMax || newPlan.RetentionNumberOfVersions === null || newPlan.RetentionNumberOfVersions === undefined
          ? 3
          : newPlan.RetentionNumberOfVersions,
      deleteVersionsOlderThan: helpData.isDeleteOlderThan,
      deleteVersionsOlderThanCount: helpData.timeGroup.value,
      deleteVersionsOlderThanPeriod: helpData.timeGroup.timePeriod,
      valid: true
    };

    if (newPlan.IsHybridBackup)
      RetentionPolicy.HybridRetentionPolicy = SupportMethodsForStepsFromPlan.getHybridRetentionPolicy(
        newPlan.HybridRetentionPolicy,
        isUnix,
        true
      );

    return RetentionPolicy;
  }

  public static updateRetentionPolicyFileOrSQL(newPlan, helpData, isCreate: boolean, isUnix = false): RetentionPolicyStepValue {
    const RetentionPolicy: RetentionPolicyStepValue = {
      RetentionUseDefaultSettings: newPlan.RetentionUseDefaultSettings,
      keepNumberOfVersions: !helpData.equalMax,
      RetentionNumberOfVersions:
        helpData.equalMax || newPlan.RetentionNumberOfVersions === null || newPlan.RetentionNumberOfVersions === undefined
          ? 3
          : newPlan.RetentionNumberOfVersions,
      deleteVersionsOlderThan: helpData.isDeleteOlderThan,
      deleteVersionsOlderThanCount: helpData.timeGroup.value,
      deleteVersionsOlderThanPeriod: helpData.timeGroup.timePeriod,
      // Unique for backup plan and SQL plan
      alwaysKeepLastVersion:
        isCreate || (helpData.isDeleteOlderThan && !newPlan.RetentionDeleteLastVersion) || newPlan.RetentionUseDefaultSettings,
      fromTimeSelected: newPlan.UseBackupDate ? 1 : 0,
      valid: true
    };

    if (!newPlan.IsHybridBackup) {
      RetentionPolicy.deleteIfLocallyDeleted = newPlan.DeleteCloudVersionIfDeletedLocally;
      RetentionPolicy.deleteAfter = newPlan.DeleteIfDeletedLocallyAfter;
    }

    if (!isUnix) {
      const RetDelay = newPlan.RetentionDelay && newPlan.RetentionDelay !== '00:00:00';
      const serialRetDelay = newPlan.SerializationSupportRetentionDelay && newPlan.SerializationSupportRetentionDelay !== '00:00:00';
      RetentionPolicy.delayPurgeForEnabled = RetDelay || serialRetDelay;
      RetentionPolicy.delayPurgeFor = helpData.delayGroup.value;
      RetentionPolicy.delayPurgeForSelect = helpData.delayGroup.timePeriod;
      if (!newPlan.IsHybridBackup) RetentionPolicy.doNotShowWarning = newPlan.HideDeleteFilesWarning;
    }

    if (newPlan.IsHybridBackup)
      RetentionPolicy.HybridRetentionPolicy = SupportMethodsForStepsFromPlan.getHybridRetentionPolicy(
        newPlan.HybridRetentionPolicy,
        isUnix
      );

    return RetentionPolicy;
  }

  public static updateAdvancedOptions(newPlan, storage: StorageConnection, isNBF = false, version = ''): DataForAdvancedStep {
    const existExcludeEnabled = !(newPlan.ExcludeEnabled === null || newPlan.ExcludeEnabled === undefined);
    const storageType = storage?.StorageType;
    const isAmazonS3 = storageType === StorageType.AmazonS3 || storageType === StorageType.AmazonS3Eu;
    const isNeedSyntheticFull =
      existExcludeEnabled &&
      (isNBF ? isAmazonS3 || TypesForSynthetic.isSupported(storage, version) : isAmazonS3 || TypesForSynthetic.CBF.isSupported(storage));
    const azureAccessTier = StepsDataFromPlanHelper.getAzureTier(newPlan);

    const isBadAmazonS3 = isAmazonS3 && TypesForFFI.unsupported.Amazon.includes(newPlan.StorageClass);
    const isBadAzure = storageType === StorageType.Azure && TypesForFFI.unsupported.Azure.includes(azureAccessTier);

    return {
      advancedOptionsStep: {
        UseS3Acceleration: isAmazonS3 && newPlan.UseS3Acceleration,
        StorageClass: !(newPlan.StorageClass === null || newPlan.StorageClass === undefined) ? newPlan.StorageClass : 1,
        azureAccessTier,
        ExcludeEnabled: newPlan.ExcludeEnabled,
        excludeRules: newPlan.DiskInfo ? SupportMethodsForStepsFromPlan.getExcludeRules(newPlan.DiskInfo) : [],
        SyntheticFull: isNeedSyntheticFull && newPlan.SyntheticFull && !isBadAzure && !isBadAmazonS3,
        IgnoreBadSectors: !!newPlan.IgnoreBadSectors,
        DisableVSS: !!newPlan.DisableVSS,
        useSystemVSSProvider: newPlan.VSSProviderID !== GuidEmpty,
        PrefetchBlockCount: newPlan.PrefetchBlockCount ? newPlan.PrefetchBlockCount : 0,
        BlockSize: newPlan.BlockSize,
        valid: true
      },
      advancedOptionsFlags: {
        ExcludePossible: existExcludeEnabled,
        UseS3AccelerationPossible: isAmazonS3,
        AzureAccessTierType: storageType === StorageType.Azure,
        StorageClassSettingPossible: !(newPlan.StorageClass === null || newPlan.StorageClass === undefined),
        SyntheticFullSettingPossible: !newPlan.IsHybridBackup && isNeedSyntheticFull && newPlan.SyntheticFull !== undefined,
        IgnoreBadSectorsPossible: !(newPlan.IgnoreBadSectors === null || newPlan.IgnoreBadSectors === undefined),
        DisableVSSPossible: !(newPlan.DisableVSS === null || newPlan.DisableVSS === undefined),
        UseVSSPossible: !(newPlan.VSSProviderID === null || newPlan.VSSProviderID === undefined),
        PrefetchBlockCountPossible: !(newPlan.PrefetchBlockCount === null || newPlan.PrefetchBlockCount === undefined),
        BlockSizeSettingPossible: !(newPlan.BlockSize === null || newPlan.BlockSize === undefined)
      }
    };
  }

  public static updateAdvancedOptionsForBackup(
    newPlan,
    storage: StorageConnection,
    isUnix = false,
    isNBF = false,
    version?: string,
    isNextBuild = false
  ): DataForAdvancedStep {
    const storageType = storage?.StorageType;
    const isAmazonS3 = storageType === StorageType.AmazonS3 || storageType === StorageType.AmazonS3Eu;
    const isNeedSyntheticFull =
      (!isUnix || isNextBuild) &&
      (isNBF ? isAmazonS3 || TypesForSynthetic.isSupported(storage, version) : isAmazonS3 || TypesForSynthetic.CBF.isSupported(storage));
    const azureAccessTier = StepsDataFromPlanHelper.getAzureTier(newPlan);

    const isBadAmazonS3 = isAmazonS3 && TypesForFFI.unsupported.Amazon.includes(newPlan.StorageClass);
    const isBadAzure = storageType === StorageType.Azure && TypesForFFI.unsupported.Azure.includes(azureAccessTier);

    const step: AdvancedOptionsStepValue = {
      azureAccessTier,
      StorageClass: !(newPlan.StorageClass === null || newPlan.StorageClass === undefined) ? newPlan.StorageClass : 1,
      UseS3Acceleration: isAmazonS3 && newPlan.UseS3Acceleration,
      SyntheticFull: isNeedSyntheticFull && newPlan.SyntheticFull && !isBadAzure && !isBadAmazonS3,
      valid: true
    };

    if (!isUnix || isNextBuild) {
      step.SaveDeletedDataInCloud = newPlan.SaveDeletedDataInCloud;
    }

    if (!isUnix) {
      step.AlwaysUseVSS = newPlan.AlwaysUseVSS || newPlan.UseFastNtfsScan;
      step.BackupNTFSPermissions = newPlan.BackupNTFSPermissions;
      step.UseFastNtfsScan = newPlan.UseFastNtfsScan;
      step.useSystemVSSProvider = newPlan.VSSProviderID !== GuidEmpty;
    }

    return {
      advancedOptionsStep: step,
      advancedOptionsFlags: {
        AlwaysUseVSSPossible: !isUnix && !(newPlan.AlwaysUseVSS === null || newPlan.AlwaysUseVSS === undefined),
        AzureAccessTierType: storageType === StorageType.Azure,
        BackupNTFSPermissionsPossible: !isUnix && !(newPlan.BackupNTFSPermissions === null || newPlan.BackupNTFSPermissions === undefined),
        StorageClassSettingPossible: isAmazonS3 && !(newPlan.StorageClass === null || newPlan.StorageClass === undefined),
        SyntheticFullSettingPossible: isNeedSyntheticFull && newPlan.SyntheticFull === false,
        UseFastNtfsScanPossible: !isUnix && !(newPlan.UseFastNtfsScan === null || newPlan.UseFastNtfsScan === undefined),
        UseS3AccelerationPossible: isAmazonS3 && !(newPlan.UseS3Acceleration === null || newPlan.UseS3Acceleration === undefined),
        UseVSSPossible: !isUnix && !(newPlan.VSSProviderID === null || newPlan.VSSProviderID === undefined)
      }
    };
  }

  public static updateAdvancedFilter(newPlan: any, isUnix = false): AdvancedFilterStepValue {
    const separator = '; ';
    const filters = newPlan.BackupFilter?.Filters?.filter((s) => !!s).join(separator);
    const isEqual = +newPlan.MaxFileSize === +MaxFileSizeStrFormat;
    const isGreaterOrEqual = isEqual || firstNumberGreaterThanSecondIfNotFloat(newPlan.MaxFileSize, MaxFileSizeStrFormat);
    const step: AdvancedFilterStepValue = {
      backupAllFiles: newPlan.BackupFilter?.FilterType,
      includeMask: filters,
      excludeMask: filters,
      skipFolders: newPlan.ExcludeFodlerList?.length > 0,
      BackupEmptyFolders: newPlan.BackupEmptyFolders,
      skipFolderName: newPlan.ExcludeFodlerList?.filter((s) => !!s).join(separator),
      ignoreSystemAndHidden: !newPlan.BackupFilter?.IncludeSystemAndHidden,
      SkipInUseFiles: newPlan.SkipInUseFiles,
      valid: true
    };

    if (!isUnix) {
      const withoutZDate = getDateWithoutZ(newPlan.BackupOnlyAfterUTC);
      const isNotMinDate = moment(withoutZDate).format(baseDateFormat) !== MinDate;
      const date = isNotMinDate ? moment(newPlan.BackupOnlyAfterUTC).format(dateFormatWithoutT) : moment().format(dateFormatWithoutT);
      step.backupFilesModified = newPlan.BackupOnlyModifiedDaysAgo !== 0;
      step.daysAgo = newPlan.BackupOnlyModifiedDaysAgo !== 0 ? newPlan.BackupOnlyModifiedDaysAgo : 14;
      step.backupFilesModifiedSince = isNotMinDate;
      step.date = date;
      step.time = moment(date, dateFormatWithoutT).format('HH:mm:ss');
      step.doNotBackup = !isEqual;
      step.fileSize = !newPlan.MaxFileSize ? 1024 : isGreaterOrEqual ? 1024 : newPlan.MaxFileSize / 1024 / 1024;
      step.ignoreOnDemand = !newPlan.BackupFilter.IncludeRecallOnDataAccessAttribute;
    }

    return step;
  }

  public static updateSchedule(newPlan, overdueAfterSuccess, isCreate = false, isUnix = false, isRestoreOrSQL = false): ScheduleStepValue {
    const Schedule = isRestoreOrSQL ? newPlan.Schedule : newPlan.ForceFullSchedule;
    const isSchedule = Schedule && Schedule.Enabled;
    const isOtherSchedule =
      (isRestoreOrSQL ? newPlan.ForceFullSchedule && newPlan.ForceFullSchedule.Enabled : newPlan.Schedule && newPlan.Schedule.Enabled) ||
      (newPlan.ScheduleDiff && newPlan.ScheduleDiff.Enabled) ||
      (newPlan.ScheduleTLog && newPlan.ScheduleTLog.Enabled);
    const isStopAfter =
      (Schedule.StopAfter && Schedule.StopAfter !== TimeSpanMaxValue) ||
      (Schedule.StopAfterTicks && Schedule.StopAfterTicks.toString() !== MaxTicks);
    const newDuration = this.getDurationForSchedule(Schedule, isStopAfter);

    const scheduleType = SupportMethodsForStepsFromPlan.getScheduleType(
      newPlan.IsPredefinedTemplatesSchedule,
      isSchedule,
      isOtherSchedule,
      Schedule.RecurType,
      isUnix
    );
    const step: ScheduleStepValue = {
      ScheduleType: scheduleType,
      specificDateGroup: SupportMethodsForStepsFromPlan.setSpecificDateGroup(
        !isCreate && isSchedule && Schedule.RecurType === RecurType.Once,
        Schedule.OnceDate
      ),
      StopAfterEnabled: isStopAfter,
      stopAfterHours: Math.floor(newDuration.asHours()),
      stopAfterMinutes: newDuration.minutes(),
      ForceMissedSchedule: !!newPlan.ForceMissedSchedule,
      syncBeforeRun: newPlan.SyncBeforeRun,
      valid: true
    };
    ScheduleDataHelper.updateOverdueFieldsOnStep(step, overdueAfterSuccess, isCreate);
    return step;
  }

  public static updateNBFSchedule(
    newPlan,
    overdueAfterSuccess,
    isCreate = false,
    isUnix = false,
    isNBF = false,
    validVersionForSchedule = false
  ): ScheduleStepValue {
    const incremental = newPlan.Schedule;
    const full = newPlan.ForceFullSchedule;
    const isSchedule = incremental?.Enabled || full?.Enabled;
    const isFFI = newPlan.ForwardIncremental && incremental?.Enabled; // Now like at agent side
    const isStopAfter =
      (incremental.StopAfter && incremental.StopAfter !== TimeSpanMaxValue) ||
      (incremental.StopAfterTicks && incremental.StopAfterTicks.toString() !== MaxTicks);
    const newDuration = this.getDurationForSchedule(incremental, isStopAfter);
    let scheduleType: keyof typeof ScheduleType = isSchedule ? 'predefined' : 'noschedule';

    if (isFFI) {
      scheduleType = 'ffi';
    }

    const step: ScheduleStepValue = {
      ScheduleType: scheduleType,
      StopAfterEnabled: isStopAfter,
      stopAfterHours: Math.floor(newDuration.asHours()),
      stopAfterMinutes: newDuration.minutes(),
      ForceMissedSchedule: !!newPlan.ForceMissedSchedule,
      incrementalData: ScheduleDataHelper.getIncrementalSchedule(incremental, validVersionForSchedule || isFFI, isUnix, isFFI, isCreate),
      fullBackupData: ScheduleDataHelper.getFullSchedule(full, validVersionForSchedule, isUnix),
      syntheticData: ScheduleDataHelper.getSyntheticSchedule(newPlan),
      syntheticRequired: !isCreate && isFFI && !newPlan.FfiSyntheticSchedule,
      syncBeforeRun: newPlan.SyncBeforeRun,
      valid: true
    };

    if (isCreate && isNBF && !isUnix) {
      step.ScheduleType = null;
      step.incrementalData = {
        ...step.incrementalData,
        enabled: true,
        recurringPeriod: 'Daily',
        repeatEveryCount: 1,
        weeklyDayOfWeek: [true, true, true, true, true, true, true],
        dailyFreqPeriodOption: 'OccursAt',
        occursAtTime: '00:00',
        occursEveryCount: 1,
        occursEveryPeriod: 'hours',
        occursEveryFromTime: '09:00',
        occursEveryToTime: '21:00'
      };
      step.fullBackupData = {
        ...step.fullBackupData,
        enabled: true,
        recurringPeriod: 'Monthly',
        occursAtTime: '00:00',
        weeklyDayOfWeek: [true, false, false, false, false, false, false]
      };
      step.syntheticData = { ...step.syntheticData, runMissed: true };
    }

    ScheduleDataHelper.updateOverdueFieldsOnStep(step, overdueAfterSuccess, isCreate);

    return step;
  }

  public static updatePrePostActions(newPlan, isRestore = false, isUnix = false): PrePostActionsStepValue {
    const PreAction = newPlan.Actions ? newPlan.Actions.Pre : null;
    const PostAction = newPlan.Actions ? newPlan.Actions.Post : null;
    const step: PrePostActionsStepValue = {
      executeCommandBeforeBackup: PreAction ? PreAction.Enabled : false,
      preAction: SupportMethodsForStepsFromPlan.getActionString(PreAction),
      exitOnPreActionFail: PreAction ? PreAction.TerminateOnFailure : true,
      executeCommandAfterBackupCompletion: PostAction ? PostAction.Enabled : false,
      postAction: SupportMethodsForStepsFromPlan.getActionString(PostAction),
      executePostActionOnSuccess: PostAction ? !PostAction.RunOnBackupFailure : true,
      valid: true
    };
    if (!isUnix && !isRestore) {
      step.enableBackupChain = newPlan.ExecuteNextPlan;
      step.backupChainNextPlan = newPlan.NextExectutionPlan || '';
      step.executeChainPlanOnSuccess = newPlan.ExecuteNextPlanOnlyIfSucces;
      step.ForceFullNextPlan = !!newPlan.ForceFullNextPlan;
    }
    return step;
  }

  public static updateAvailableMachinePlans(AMPlans, id: string): DefaultSelectData[] {
    return AMPlans
      ? Object.keys(AMPlans)
          .filter((i) => i !== id)
          .map((key) => ({ value: key, label: AMPlans[key] }))
      : [];
  }

  public static updateNotification(Notification): NotificationsStepValue {
    const step: NotificationsStepValue = {
      receiveNotificationOnCompleteGroup: {
        flag: Notification.SendNotification,
        case: Notification.OnlyOnFailure ? 'whenFails' : 'allCases'
      },
      valid: true
    };
    return step;
  }

  public static updateAdvancedScheduleBasedOnMode(
    newPlan,
    isDefault = false,
    planType: PlanTypes,
    isUnix = false
  ): ScheduleAdvancedStepValue {
    if (!isDefault) {
      return this.updateAdvancedSchedule(newPlan, planType, isUnix);
    } else return this.advancedScheduleDefault(planType, isUnix);
  }

  public static advancedScheduleDefault(planType: PlanTypes, isUnix = false): ScheduleAdvancedStepValue {
    const isSQL = planType === PlanTypes.DataBasePlan;
    const isCheck = planType === PlanTypes.ConsistencyCheck;
    const step: ScheduleAdvancedStepValue = {
      forceFull: new ScheduleFormSettings(),
      blockLevel: isSQL || isCheck ? new ScheduleFormSettings(null, null) : new ScheduleFormSettings(),
      scheduleDiff: isSQL ? new ScheduleFormSettings() : new ScheduleFormSettings(null, null),
      scheduleTLog: isSQL ? new ScheduleFormSettings() : new ScheduleFormSettings(null, null),
      valid: false
    };

    if (!isUnix && planType === PlanTypes.Plan) {
      step.ForceFullApplyDiffSizeCondition = false;
      step.ForceFullDiffSizeCondition = 50;
    }

    return step;
  }

  public static updateSimpleSchedule(newPlan, planType: PlanTypes = PlanTypes.Plan): SimpleScheduleStepValue {
    const isSQL = planType === PlanTypes.DataBasePlan;
    const full = isSQL ? newPlan.Schedule : newPlan.ForceFullSchedule;
    const block = isSQL ? new ScheduleSettings() : newPlan.Schedule;
    const isFull = full?.Enabled;
    const fullSchedule = isFull ? full : new ScheduleSettings();
    const isBlock = block?.Enabled;
    const blockLevel = isBlock ? block : new ScheduleSettings();
    const isDiff = !!newPlan?.ScheduleDiff?.Enabled;
    const isTLog = !!newPlan?.ScheduleTLog?.Enabled;
    const dateNow = SupportMethodsForStepsFromPlan.getOnceDateFromSchedule(isBlock ? blockLevel : fullSchedule);

    return {
      startFromDate: dateNow,
      startFromTime: moment(dateNow).format('HH:mm:ss'),
      scheduleTemplate: '',
      fullScheduleEnabled: isFull,
      fullBackupEveryHours: isFull ? fullSchedule.Hour : 0,
      fullBackupEveryMinutes: isFull ? fullSchedule.Minutes : 0,
      blockLevelBackup: isBlock,
      blockLevelBackupEveryHours: isBlock ? blockLevel.Hour : 0,
      blockLevelBackupEveryMinutes: isBlock ? blockLevel.Minutes : 0,
      scheduleDiffEnabled: isDiff,
      differentialBackupEveryHours: isDiff ? newPlan.ScheduleDiff.Hour : 0,
      differentialBackupEveryMinutes: isDiff ? newPlan.ScheduleDiff.Minutes : 0,
      scheduleTLogEnabled: isTLog,
      tLogBackupEveryHours: isTLog ? newPlan.ScheduleTLog.Hour : 0,
      tLogBackupEveryMinutes: isTLog ? newPlan.ScheduleTLog.Minutes : 0,
      valid: true
    };
  }

  public static updateClusterStep(newPlan, create = true): ClusterStepValue {
    const step: ClusterStepValue = {
      clusterBackup: !!newPlan.ClusterBackup,
      login: newPlan.Login || '',
      password: create || !newPlan.ClusterBackup ? '' : newPlan.Password || '',
      remember: false,
      skipCredentials: false,
      valid: true
    };

    return step;
  }

  public static updateSelectHost(newPlan, isEdit = true): SelectHostStepValue {
    const stepValue: SelectHostStepValue = {
      server: newPlan.Host,
      login: newPlan.Login,
      password: isEdit ? newPlan.Password || '' : '',
      valid: false
    };

    if (newPlan.RestoreToHost) stepValue.host = newPlan.RestoreToHost;

    return stepValue;
  }

  public static updateSelectHostIBB(newPlan, isEdit = true, isProxmox = false): SelectHostStepValue {
    const name = isProxmox ? 'RestoreToProxmox' : 'RestoreToVMware';

    if (!newPlan[name]) return { server: '', login: '', password: '', valid: false };

    const stepValue: SelectHostStepValue = {
      server: newPlan[name].Host,
      login: newPlan[name].Login,
      password: isEdit ? newPlan[name].PasswordEncrypted || '' : '',
      valid: false
    };

    if (newPlan[name].RestoreToHost) stepValue.host = newPlan[name].RestoreToHost;

    return stepValue;
  }

  public static updateVmSettingsStep(newPlan, isProxmox = false): VmSettingsStepValue {
    const machineConf = newPlan[isProxmox ? 'RestoreToProxmox' : 'RestoreToVMware'];

    const step: VmSettingsStepValue = {
      vmName: machineConf?.VmName,
      numCPU: machineConf?.NumCPUs,
      memorySize: machineConf?.MemoryMB,
      datastore: machineConf?.Datastore,
      overwrite: !!machineConf?.Overwrite,
      powerOn: machineConf?.PowerOnAfterRestore,
      valid: true
    };

    if (isProxmox) {
      step.guestOs = machineConf?.GuestOsType;
      step.virtualNetwork = machineConf?.Network?.Bridge;
      step.cardModel = machineConf?.Network?.Model || 0;
      step.vmId = machineConf?.VmUuid;
    } else {
      step.guestOs = machineConf?.GuestId;
      step.virtualNetwork = machineConf?.Network;
      step.diskProvision = machineConf?.ThinProvisioned
        ? ProvisionModeEnum.thin
        : machineConf?.EagerlyScrub
          ? ProvisionModeEnum.eager
          : ProvisionModeEnum.lazy;
    }

    return step;
  }

  public static updateHostForInstantRestore(newPlan): SelectHostStepValue {
    if (!newPlan?.TestVmSettings) return { server: '', login: '', password: '', valid: false };

    const stepValue: SelectHostStepValue = {
      server: newPlan?.TestVmSettings.Host,
      login: newPlan?.TestVmSettings.LoginPassword.Login,
      password: newPlan?.TestVmSettings.LoginPassword.Password || '',
      valid: false
    };

    if (newPlan?.TestVmSettings.Node) stepValue.host = newPlan?.TestVmSettings.Node;

    return stepValue;
  }

  public static updateVmSettingsForInstantRestore(newPlan, isProxmox = false): VmSettingsStepValue {
    const stepData: VmSettingsStepValue = {
      vmName: newPlan?.TestVmSettings.VmName,
      numCPU: newPlan?.TestVmSettings.NumCpu,
      memorySize: newPlan?.TestVmSettings.MemoryInMb,
      virtualNetwork: newPlan?.TestVmSettings.Network,
      maxMemorySize: newPlan?.TestVmSettings.MaxMemoryInMb,
      minMemorySize: newPlan?.TestVmSettings.MinMemoryInMb,
      dynamic: newPlan?.TestVmSettings.DynamicMemoryEnabled,
      generation: newPlan?.TestVmSettings.GenerationIsV2,
      powerOn: newPlan?.TestVmSettings.PowerOnAfterRestore,
      overwrite: newPlan?.TestVmSettings.Overwrite,
      valid: true
    };

    if (isProxmox) {
      stepData.guestOs = newPlan?.TestVmSettings?.GuestOsType;
      stepData.virtualNetwork = newPlan?.TestVmSettings?.Network?.Bridge;
      stepData.cardModel = newPlan?.TestVmSettings?.Network?.Model || 0;
      stepData.vmId = newPlan?.TestVmSettings?.VmUuid;
    } else {
      stepData.guestOs = newPlan?.TestVmSettings?.GuestOsType;
      stepData.virtualNetwork = newPlan?.TestVmSettings?.Network;
      stepData.destination = newPlan?.TestVmSettings.DestinationFolder || newPlan?.TestVmSettings.Destination || '';
    }

    return stepData;
  }

  public static updateVmSettingsHyperVStep(newPlan): VmSettingsStepValue {
    const stepData: VmSettingsStepValue = {
      vmName: newPlan?.RestoreToHyperV.VmName,
      numCPU: newPlan?.RestoreToHyperV.NumCpu,
      memorySize: newPlan?.RestoreToHyperV.MemoryInMb,
      virtualNetwork: newPlan?.RestoreToHyperV.NetworkSwitch,
      maxMemorySize: newPlan?.RestoreToHyperV.MaxMemoryInMb,
      minMemorySize: newPlan?.RestoreToHyperV.MinMemoryInMb,
      destination: newPlan?.RestoreToHyperV.Destination,
      dynamic: newPlan?.RestoreToHyperV.DynamicMemoryEnabled,
      generation: newPlan?.RestoreToHyperV.IsV2,
      powerOn: newPlan?.RestoreToHyperV.PowerOnVm,
      overwrite: newPlan?.RestoreToHyperV.Overwrite,
      valid: true
    };

    return stepData;
  }

  public static updateSelectVirtualMachines(newPlan, isProxmox = false): SelectVirtualMachinesStepValue {
    let newType = VirtualMachinesSelectedType.All;
    if (newPlan.VMBackupType) {
      newType =
        typeof newPlan.VMBackupType === 'string'
          ? newPlan.VMBackupType
          : (VirtualMachinesSelectedWithNumbersType[newPlan.VMBackupType] as VirtualMachinesSelectedType);
    }

    return {
      type: newType,
      machines: isProxmox ? newPlan.ProxmoxMachines.map((m) => m.DisplayName) : newPlan.VirtualMachines || [],
      valid: false
    };
  }

  public static updateSelectAppAware(newPlan): AppAwareStepValue {
    return {
      settings: (newPlan.AppAwareGuestVmIndividualSettings || []).map((item) => {
        return {
          virtualMachineId: null, // now we don`t know this information
          VMName: item.VMName,
          processingMode: item.QuiesceType
        };
      }),
      valid: true
    };
  }

  public static getExcludesFromDiskVolumes(disk: DiskInfoCommunication): string[] {
    const excludes: string[] = [];

    if (disk.Volumes) {
      disk.Volumes.forEach((volume) => {
        if (!volume.Enabled) {
          excludes.push(
            SupportMethodsForStepsFromPlan.getExcludeRuleString(
              volume.MountPoints,
              volume.WindowsVolumeIdentity || volume.Identity,
              '',
              true
            )
          );
        } else if (SupportMethodsForStepsFromPlan.needUpdateExcludeRules(volume)) {
          volume.BackupOptions.ExcludeRules.forEach((excl) => {
            const excludeRule = SupportMethodsForStepsFromPlan.getExcludeRuleString(
              volume.MountPoints,
              volume.WindowsVolumeIdentity || volume.Identity,
              excl.Folder === '\\' ? '' : excl.Folder,
              true
            );

            excludes.push(excludeRule);
          });
        }
      });
    }

    return excludes;
  }

  public static updateSelectVirtualDisks(newPlan, isVMWare = false): SelectVirtualDisksStepValue {
    const machines = isVMWare ? newPlan.VirtualMachinesEx : newPlan.HyperVMachinesEx;

    return {
      type: !machines?.length ? VirtualDisksSelectedType.All : newPlan.Type || VirtualDisksSelectedType.Selected,
      disks:
        (isVMWare
          ? this.getVMWareDisksForSelectedDisksStep(machines)
          : this.getHyperVDisksForSelectedDisksStep(machines, newPlan.VirtualMachinesEx || [])) || [],
      allDisks: this.getAllDisksFromPlan(isVMWare, machines),
      valid: false
    };
  }

  public static updateAdvancedSettingsVirtual(
    newPlan,
    storage: StorageConnection,
    isCreate: boolean,
    isVMWare = false,
    version: string
  ): AdvancedSettingsVirtualStepValue {
    const storageType = storage?.StorageType;
    const azureAccessTier = StepsDataFromPlanHelper.getAzureTier(newPlan);
    const isBadAmazonS3 =
      (storageType === StorageType.AmazonS3 || storageType === StorageType.AmazonS3Eu) &&
      TypesForFFI.unsupported.Amazon.includes(newPlan.StorageClass);
    const isBadAzure = storageType === StorageType.Azure && TypesForFFI.unsupported.Azure.includes(azureAccessTier);

    const stepData: AdvancedSettingsVirtualStepValue = {
      useChangeBlockTracking: newPlan.UseChangeBlockTracking,
      useS3Acceleration: newPlan.UseS3Acceleration,
      storageClass: newPlan.StorageClass || 1,
      azureAccessTier,
      syntheticFull:
        isCreate || (newPlan.SyntheticFull && !isBadAmazonS3 && !isBadAzure && TypesForSynthetic.isSupported(storage, version)),
      dataFromHelper: true,
      valid: true
    };

    if (isVMWare) {
      stepData.quiesceType = newPlan.QuiesceType;
    }

    return stepData;
  }

  public static updateSQLServerInstance(newPlan, isCreate = true): SQLServerInstanceStepValue {
    return {
      sqlServerInstanceName: newPlan.InstanceName || newPlan.DestinationInstanceName || '',
      authenticationType:
        newPlan.WindowsAuthentication || isCreate
          ? SQLServerInstanceAuthenticationTypeEnum['Windows Authentication']
          : SQLServerInstanceAuthenticationTypeEnum['SQL Server Authentication'],
      userName: newPlan.UserName,
      password: newPlan.Password,
      useSecureConnection: newPlan.SecureConnection,
      checkIsSysAdmin: true,
      valid: false
    };
  }

  public static updateSQLSSourceDatabases(newPlan): SQLSourceDatabasesStepValue {
    return {
      databaseBackupType: newPlan.DatabaseSelectionType,
      selectedDatabases: newPlan.Databases,
      copyOnly: newPlan.CopyOnly,
      valid: true
    };
  }

  private static getAzureTier(newPlan) {
    let azureAccessTier = AzureAccessTierType.Hot;

    if (newPlan.AzureStorageClassSerialization === AzureAccessTierType[AzureAccessTierType.Cold])
      azureAccessTier = AzureAccessTierType.Cold;
    if (newPlan.UseAzureArchive) azureAccessTier = AzureAccessTierType.Archive;
    if (newPlan.UseAzureCool) azureAccessTier = AzureAccessTierType.Cool;

    return azureAccessTier;
  }

  private static getAllDisksFromPlan(isVMWare: boolean, machines: Array<HyperVMachineExType & VirtualMachineExType>): VirtualDisk[] {
    return machines.reduce((disks, machine) => {
      const diskInfo = isVMWare ? machine.DiskInfo : machine.SnapshotConfig.Disks;
      const machineUUID = isVMWare ? machine.UUID : machine.VMUUID;

      diskInfo.forEach((d) => {
        d.uiGuid = isVMWare ? d.DiskId + machineUUID : d.UUID + machineUUID;
        disks.push(d);
      });

      return disks;
    }, []);
  }

  private static getHyperVDisksForSelectedDisksStep(machines: HyperVMachineExType[], withExcludes: VirtualMachineExType[]): VirtualDisk[] {
    const volumesMap = withExcludes.reduce((result: any, item: VirtualMachineExType) => {
      item.DiskInfo.forEach((disk: DiskInfoCommunication) => (result[disk.DiskId + item.UUID] = disk));
      return result;
    }, {});

    return machines.reduce((disks, machine) => {
      machine.SnapshotConfig?.Disks?.forEach((disk) => {
        if (disk.Enabled) {
          disks.push({
            uuid: disk.UUID,
            uiGuid: disk.UUID + machine.VMUUID,
            fileName: disk.Path,
            label: 'Label',
            changeId: 'ChangeId',
            deviceKey: 0,
            diskCapacity: 0,
            virtualMachineName: machine.VMName,
            virtualMachineId: machine.VMUUID,
            id: disk.UUID,
            rootDiskId: GuidEmpty,
            excludes: volumesMap[disk.UUID + machine.VMUUID] ? this.getExcludesFromDiskVolumes(volumesMap[disk.UUID + machine.VMUUID]) : []
          });
        }
      });

      return disks;
    }, []);
  }

  private static getVMWareDisksForSelectedDisksStep(machines: VirtualMachineExType[]): VirtualDisk[] {
    return machines.reduce((disks, machine) => {
      machine?.DiskInfo?.forEach((disk) => {
        if (disk.Enabled) {
          disks.push({
            uuid: disk.DiskId,
            uiGuid: disk.DiskId + machine.UUID,
            fileName: disk.DiskName,
            label: 'Label',
            changeId: 'ChangeId',
            deviceKey: 0,
            diskCapacity: 0,
            virtualMachineName: machine.VMName,
            virtualMachineId: machine.UUID,
            id: disk.DiskId,
            rootDiskId: GuidEmpty,
            excludes: this.getExcludesFromDiskVolumes(disk)
          });
        }
      });

      return disks;
    }, []);
  }

  private static updateAdvancedSchedule(newPlan, planType: PlanTypes, isUnix = false): ScheduleAdvancedStepValue {
    const isRestore = RestoreTypes.includes(planType);
    const isSQL = planType === PlanTypes.DataBasePlan;
    const isCheck = planType === PlanTypes.ConsistencyCheck;
    const full = isSQL || isCheck || isRestore ? newPlan.Schedule : newPlan.ForceFullSchedule;

    const step: ScheduleAdvancedStepValue = {
      forceFull: {
        Enabled: full?.Enabled,
        FormSchedule: ScheduleDataHelper.generateNewRecurringSchedule(full, isUnix)
      },
      blockLevel:
        isSQL || isCheck
          ? new ScheduleFormSettings(null, null)
          : {
              Enabled: isRestore ? false : newPlan?.Schedule?.Enabled,
              FormSchedule:
                !isRestore && !isSQL && newPlan?.Schedule?.Enabled
                  ? ScheduleDataHelper.generateNewRecurringSchedule(newPlan.Schedule, isUnix)
                  : new RecurringSchedule()
            },
      scheduleDiff:
        planType === PlanTypes.DataBasePlan
          ? SupportMethodsForStepsFromPlan.getScheduleDiffOrTLog(newPlan.ScheduleDiff)
          : new ScheduleFormSettings(null, null),
      scheduleTLog:
        planType === PlanTypes.DataBasePlan
          ? SupportMethodsForStepsFromPlan.getScheduleDiffOrTLog(newPlan.ScheduleTLog)
          : new ScheduleFormSettings(null, null),
      valid: true
    };

    if (planType === PlanTypes.Plan) {
      step.ForceFullApplyDiffSizeCondition = newPlan.ForceFullApplyDiffSizeCondition;
      step.ForceFullDiffSizeCondition = newPlan.ForceFullDiffSizeCondition;
    }

    return step;
  }

  private static getDurationForSchedule(schedule: ScheduleSettings, isStopAfter: boolean): Duration {
    return isStopAfter
      ? schedule.StopAfter
        ? moment.duration(schedule.StopAfter)
        : moment.duration(+schedule.StopAfterTicks / 10000, 'milliseconds')
      : moment.duration({ hours: 0 });
  }
}
